import ListingCard from "components/ClientSearch/Listings/ListingCard";
import gql from "graphql-tag";
import { memoize, noop } from "lodash";
import Link from "next/link";
import React from "react";
import { useQuery } from "@apollo/client";
import SkeletonBlock from "components/Skeleton/Block";
import { constantCase } from "util/strings";
import styles from "./styles.module.scss";
import { SimliarOfficesNearBy } from "./types/SimliarOfficesNearBy";

type Props = {
  isLoggedIn: boolean;
  buildingId: string;
  type: "Listing" | "Building";
};

const SIMILAR_OFFICES = gql`
  query SimliarOfficesNearBy($id: ID) {
    buildingOrRedirect(id: $id) {
      building {
        id
        slug
        nearbyListingsRanked(limit: 4) {
          id
          building {
            id
            identity
            slug
            address
            minSquareFeet
            maxSquareFeet
            minEstimatedMonthlyPrice
            maxEstimatedMonthlyPrice
            minEstimatedSeats
            maxEstimatedSeats
            city
            image2 {
              id
              url
            }
            neighborhood {
              id
              externalId
              name
            }
            submarket {
              id
              externalId
              name
            }
            market {
              id
              externalId
              name
            }
          }
          floorAndSuite
          brokerInsights {
            id
            content
          }
          status
          squareFeet
          estimatedSquareFeet
          estimatedPsf
          accuratePsf
          estimatedMaxOccupancy
          maxOccupancy
          estimatedMonthlyPrice
          accurateMonthlyPrice
          displayMonthlyPrice
          displayPSF
          leaseTerm
          leaseType
          image2 {
            id
            url
          }
        }
      }
    }
  }
`;

const SimliarOffices = ({ isLoggedIn, buildingId, type }: Props) => {
  const { data, loading } = useQuery<SimliarOfficesNearBy>(SIMILAR_OFFICES, {
    variables: {
      id: buildingId,
    },
  });

  if (loading)
    return (
      <>
        <p className={`bodyEmphasis ${styles.title}`}>Similar Offices Nearby</p>
        <ul className={styles.cardList}>
          <SkeletonBlock className={styles.skeleton} />
          <SkeletonBlock className={styles.skeleton} />
          <SkeletonBlock className={styles.skeleton} />
          <SkeletonBlock className={styles.skeleton} />
        </ul>
      </>
    );
  if (!data) return null;
  const building = data.buildingOrRedirect?.building;
  const listings = building?.nearbyListingsRanked;

  if (!listings?.length) return null;
  const nonNullListings = listings.flatMap((l) => (l ? [l] : []));
  const getLink = memoize((id, slug, uuid) => {
    if (isLoggedIn) {
      return `/tourbook/listing/${id}`;
    }
    return `/building${slug}/${uuid}?listingId=${id}`;
  });

  return (
    <>
      <p className={`bodyEmphasis ${styles.title}`}>Similar Offices Nearby</p>
      <ul className={styles.cardList}>
        {nonNullListings.map((listing) => {
          const { building: b } = listing;
          return (
            <li key={listing.id} className={styles.card}>
              <Link passHref href={getLink(listing.id, b.slug, b.identity)}>
                <a>
                  <ListingCard
                    className={styles.card}
                    listing={listing}
                    signUpType={type}
                    source="Simliar Office Bookmark"
                    areaExternalIds={
                      b.neighborhood?.externalId
                        ? [b.neighborhood?.externalId]
                        : []
                    }
                    marketOfInterest={b.market?.externalId}
                    cityOfInterest={constantCase(b.city)}
                    onSuccess={noop}
                  />
                </a>
              </Link>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default SimliarOffices;
